import * as React from 'react';
import { reduxForm, InjectedFormProps, Field, SubmissionError } from 'redux-form';
const { connect } = require('react-redux');
import * as c from 'models/constants';
import { EllipseGreyBlackButton } from 'components/Common/Button';
import { ICropImageObject, ICropperOptions, IImageData, ITranslateProps } from 'models/common';
import { ImageUpload, PortalInputField, PortalTextareaField } from 'components/FormControls';
import { maxLength, required } from 'helpers/validationattributes';
import { translate } from 'react-i18next';
import { Modal } from 'components';
import { ButtonsSection } from 'components/PortalStructure';
import { Button, WhiteButton } from 'components';
import renderIcon from 'helpers/renderIcon';
import { actions as notifActions } from 'redux-notifications';
import { CroppedImage } from 'components/Common/CroppedImage';
import { submitForm } from 'helpers/common';
require('./style.scss');

const maxLength500 = maxLength(500);

export interface IStaffMember {
	name?: string;
	jobtitle?: string;
	about?: string;
	picture?: string;
}

interface IProps extends InjectedFormProps, ITranslateProps {
	closeModal?: () => Promise<any>;
	onSubmitCallback: any;

	initialValues: IStaffMember;
	currentFormValues: any;

	// injected form props
	pristine: boolean; //if fields are untuched
	submitting: boolean;
	handleSubmit: any;
	change: (field: string, value: any) => void;

	// dispatch to props
	showNotification: (message: string, kind: string, dismissAfter?: number) => any;
	saveImage: (image: IImageData) => Promise<any>;
}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class EditStaffMemberModalForm extends React.Component<IProps, any> {
	constructor(props) {
		super(props);

		this.closeModal = this.closeModal.bind(this);
		this.handleEditButtonClick = this.handleEditButtonClick.bind(this);
		this.handleUploadButtonClick = this.handleUploadButtonClick.bind(this);
		this.onSubmit = this.onSubmit.bind(this);
		this.croppedImageCallback = this.croppedImageCallback.bind(this);
		this.mainImageUploadedCallback = this.mainImageUploadedCallback.bind(this);
		this.imageUploadFailedCallback = this.imageUploadFailedCallback.bind(this);
		this.renderImageUpload = this.renderImageUpload.bind(this);
		this.onFileDialogCancel = this.onFileDialogCancel.bind(this);

		this.imageInputRef = React.createRef();
		this.croppedPhotoRef = React.createRef();

		this.state = {
			editButtonClicked: false,
			uploadButtonClicked: false,
			imageData: null,
			imageUploadError: '',
		};
	}

	private imageInputRef;
	private croppedPhotoRef;

	public componentDidMount() {}

	private formRef = React.createRef<HTMLFormElement>();

	private mainImageUploadedCallback(id: string, href: string) {
		this.props.change('picture', href);
		this.props.change('pictureId', id);
		this.setState({ editButtonClicked: true, uploadButtonClicked: false, imageUploadError: '' });
	}

	private imageUploadFailedCallback(error: any) {
		console.error(error);
		this.setState({
			editButtonClicked: false,
			uploadButtonClicked: false,
			imageUploadError: error && error.error ? error.error[0] : '',
		});
	}

	private croppedImageCallback(imageData: ICropImageObject) {
		// get original image width and height
		imageData.thumbnails = c.DEFAULT_CROP_SIZES_PROFILE_IMAGE;
		imageData.createThumbnail = true;
		imageData.id = this.props.currentFormValues?.pictureId;
		imageData.url = this.props.currentFormValues?.picture;

		this.setState({
			imageData,
		});
	}

	private handleEditButtonClick() {
		this.setState({
			editButtonClicked: true,
			imageUploadError: '',
		});
	}

	private handleUploadButtonClick() {
		if (this.imageInputRef.current) {
			this.imageInputRef.current.open();
		}
		this.setState({
			uploadButtonClicked: true,
		});
	}

	private onFileDialogCancel() {
		this.setState({
			uploadButtonClicked: false,
		});
	}

	private closeModal() {
		this.props?.closeModal();
	}

	private onSubmit(formData) {
		const imageData = this.state.imageData;
		if (imageData && (!imageData.id || imageData.id === null)) {
			// if imageData doesn't have an id, get it from imageData.url
			imageData.id = imageData.url.split('/').pop().split('.')[0];
		}
		if (imageData) {
			// if we edited/uploaded an image
			this.props
				.saveImage(imageData)
				.then((res) => {
					if (res) {
						formData.picture = res.href;
					}
					this.props.onSubmitCallback(formData).catch((e) => {
						throw new SubmissionError(e);
					});
				})
				.catch((e) => {
					throw new SubmissionError(e);
				});
		} else {
			// we edited just the text
			this.props.onSubmitCallback(formData).catch((e) => {
				throw new SubmissionError(e);
			});
		}
	}

	private renderImageUpload({ input }) {
		return (
			<div className={`photo-container ${input.value ? `` : `default-pfp`}`}>
				{input.value ? (
					<CroppedImage imageData={this.state.imageData} imageUrl={input.value} alt="Staff Profile picture" />
				) : (
					renderIcon('smile_happy')
				)}
			</div>
		);
	}

	public render() {
		// office_idx = index of office in state
		const { t, handleSubmit, pristine, submitting } = this.props;

		const footer = (
			<>
				<ButtonsSection align="right">
					{this.state.editButtonClicked ? (
						<WhiteButton
							onClick={() =>
								this.setState({
									editButtonClicked: false,
									imageData: null,
								})
							}
						>
							{t('BSPortal_Common_Cancel')}
						</WhiteButton>
					) : (
						<WhiteButton onClick={this.closeModal}>{t('BSPortal_Common_Close')}</WhiteButton>
					)}
					<Button
						type="button"
						disabled={
							this.state.uploadButtonClicked ||
							(!this.state.imageData && !this.state.editButtonClicked && (pristine || submitting)) //TODO: deisable button + crop multiple images
						}
						onClick={() => {
							if (this.state.editButtonClicked) {
								this.setState({ editButtonClicked: false });
							} else {
								// handle crop image
								// imageData is in state
								submitForm(this.formRef.current);
							}
						}}
					>
						{t('BSPortal_Common_Save')}
					</Button>
				</ButtonsSection>
			</>
		);

		const cropOptions: ICropperOptions = {
			...c.DEFAULT_CROP_OPTIONS,
			aspectRatio: c.ASPECT_RATION_SHOP_PROFILE_PICTURE,
			croppedImageCallback: this.croppedImageCallback,
			showSlider: true,
			minContainerHeight: 300,
			minContainerWidth: 300,
		};

		return (
			<Modal
				modalClass="modal-edit-staff-member"
				title={t('Shop_Shopcard_Services_EditStaff')}
				showHeaderCloseButton={true}
				closeModal={this.closeModal}
				size={'large'}
				footer={footer}
			>
				<div className="edit-staff-member-wrapper d-flex flex-column flex-lg-row align-items-center align-items-lg-start justify-content-center">
					{!this.state.editButtonClicked && (
						<>
							<div className="picture-container">
								<div className="two-part">
									<figure ref={this.croppedPhotoRef}>
										<figcaption>{t('PortalPage_Section_Manage_Common_Picture')}</figcaption>

										<Field name={`picture`} component={this.renderImageUpload} type="text" autoFocus={false} />
									</figure>
									<div className="picture-controls">
										<EllipseGreyBlackButton
											disabled={!(this.props.currentFormValues && this.props.currentFormValues.picture)}
											onClick={this.handleEditButtonClick}
										>
											{t('User_UserCard_EditCurrentPicture')}
										</EllipseGreyBlackButton>
										<EllipseGreyBlackButton onClick={this.handleUploadButtonClick}>
											{t('User_UserCard_UploadNewPicture')}
										</EllipseGreyBlackButton>
									</div>
								</div>
								<p className="p14 red">{this.state.imageUploadError}</p>
							</div>
							<div className="input-field-list">
								<form onSubmit={handleSubmit(this.onSubmit)} ref={this.formRef}>
									<Field
										name={`name`}
										component={PortalInputField}
										type="text"
										autoFocus={false}
										validate={[required]}
										label={t('Shop_ManageShop_ManageStaffForm_Name_Surname')}
									/>
									<Field
										name="jobTitle"
										component={PortalInputField}
										type="text"
										autoFocus={false}
										label={t('Shop_ManageShop_ManageStaffForm_Job_Title')}
										description={`(${t('User_ShopCard_Services_Optional').toLowerCase()})`}
									/>
									<Field
										name="description"
										component={PortalTextareaField}
										type="textarea"
										usage="admin"
										validate={[maxLength500]}
										maxLength={500}
										rows="6"
										autoFocus={false}
										label={t('Shop_ManageShop_ManageStaffForm_About')}
										description={`(${t('User_ShopCard_Services_Optional').toLowerCase()})`}
									/>
								</form>
							</div>
						</>
					)}
					<div
						style={{
							display: this.state.editButtonClicked ? `` : `none`,
						}}
					>
						<Field
							name="imageUpload"
							dropZoneRef={this.imageInputRef}
							withRef={true}
							component={ImageUpload}
							imageUploadedCallback={this.mainImageUploadedCallback}
							imageUploadFailed={this.imageUploadFailedCallback}
							onFileDialogCancel={this.onFileDialogCancel}
							additionalData={['id', 'url']}
							existingImageId={this.state.editButtonClicked ? this.props.currentFormValues?.pictureId : null}
							existingImageUrl={this.state.editButtonClicked ? this.props.currentFormValues?.picture : null}
							cropImage={true}
							cropOptions={cropOptions}
							maxImageSize={10000000}
						/>
					</div>
				</div>
			</Modal>
		);
	}
}

// inject redux form and connect to redux
export const EditStaffMemberModal = connect(
	(state) => {
		return {
			currentFormValues: state.form[c.FORM_NAME_MANAGE_OFFICE_STAFF_MEMBER]?.values,
		};
	},
	(dispatch) => ({
		showNotification: (message: string, kind: string, dismissAfter = 3000) =>
			dispatch(notifActions.notifSend({ message, kind, dismissAfter })),
	}),
)(
	reduxForm({
		form: c.FORM_NAME_MANAGE_OFFICE_STAFF_MEMBER,
		enableReinitialize: true,
	})(EditStaffMemberModalForm),
);
